import { memo } from 'react';

import { SpinnerOverlayNoRemount } from '@ctct/presentation-layer';
import { PaymentMethodStripeEditState } from '@ctct/state-client-layer';

import { StripeElementEditPayment } from './StripeElementEditPayment';
import { StripeNewPaymentMethodNonEditable } from './StripeNewPaymentMethodNonEditable';
import { StripeSavedPaymentMethod } from './StripeSavedPaymentMethod';
import type { LoadedStripePaymentMethodsWidgetProps } from './usePaymentMethodsWidget';

import './stripe-payment-widget.scss';

type Props = {
  isLoading: boolean;
} & LoadedStripePaymentMethodsWidgetProps;

export const StripePaymentWidget = memo(({ isLoading, ...props }: Props) => {
  const { data, handlers, hooks } = props;
  const stripeEditState = hooks.usePaymentMethodStripeEditState();

  let content = null as null | React.ReactNode;

  switch (stripeEditState) {
    case PaymentMethodStripeEditState.Editable:
      content = (
        <StripeElementEditPayment
          onMount={handlers.onMountStripeElementDiv}
          onCancel={data.hasSavedPaymentMethod() ? handlers.onCancelEditExistingPaymentMethod : undefined}
          isLoading={isLoading}
        />
      );
      break;
    case PaymentMethodStripeEditState.SavedPaymentMethod:
      content = <StripeSavedPaymentMethod {...props} />;
      break;
    case PaymentMethodStripeEditState.NewPaymentMethodNonEditable:
      content = <StripeNewPaymentMethodNonEditable />;
      break;
    default:
      break;
  }

  // Don't show spinner overlay when payment method is ready (NewPaymentMethodNonEditable state)
  // to avoid duplicate spinner with PaymentProcessingInfoWidget
  const shouldShowSpinner = isLoading && stripeEditState !== PaymentMethodStripeEditState.NewPaymentMethodNonEditable;

  return (
    <SpinnerOverlayNoRemount isLoading={shouldShowSpinner} className="stripe-payment-widget__spinner-overlay">
      {content}
    </SpinnerOverlayNoRemount>
  );
});
